import {
    closestRoleFromTyped,
    type KnownWorkRole,
    type QuickReply,
    quickRepliesFor,
    roleAnswerFromTyped,
    SETUP_CHAT_PURPOSE,
    type SetupQuestion,
    SOMETHING_ELSE,
    SOMETHING_ELSE_LABEL,
    WORK_PROFILE_WORK_FIRST,
    WORK_PROFILE_WORK_FIRST_TEXT,
    WORK_ROLE_LABELS,
    WORK_ROLES,
} from '@zerohuman/shared'
import type { ChatMessageRow, NamesTemplate, OnboardingStateView } from '@/api'

/** The setup conversation's header link to the guided screens (#1742). */
export const PREFER_STEP_BY_STEP = 'Prefer step-by-step?'

/** Each guided screen's link back to the setup conversation (#1742). */
export const SET_UP_WITH_COO = 'Set up with your COO instead'

/** The link that skips setup for the dashboard in Advanced mode (#1742, O12). */
export const SKIP_SETUP = 'Advanced mode: skip setup and take me to the dashboard'

/** Whether setup is still open for either path: not complete, and not skipped for the dashboard (#1742). */
export function setupIsOpen(state: OnboardingStateView | null | undefined): boolean {
    return !!state && (state.status === 'in_progress' || state.status === 'not_started')
}

/**
 * Whether setup may be skipped (#1742): it is open and the AI account step is complete, so the account is already
 * connected. The server refuses a skip before that all the same.
 */
export function setupSkippable(state: OnboardingStateView | null | undefined): boolean {
    return setupIsOpen(state) && !!state?.completedSteps.includes('ai_account')
}

/**
 * A typed role waiting to be confirmed (#1742, O13): what the person typed, the part of it the profile keeps, and the
 * known role it is closest to. Nothing is saved until they accept it or pick another role.
 */
export interface RoleGuess {
    typed: string
    kept: string
    matched: KnownWorkRole
}

/**
 * The guess a typed role needs, or null when it needs none: text naming a role's chip saves that role at once, and
 * blank text is no answer. Anything else is matched to the closest known role (`closestRoleFromTyped`) and confirmed.
 */
export function roleGuessFor(typed: string): RoleGuess | null {
    const answer = roleAnswerFromTyped(typed)
    if (answer?.role !== 'other') {
        return null
    }
    const matched = closestRoleFromTyped(typed)
    return matched ? { typed: typed.trim(), kept: answer.text, matched } : null
}

/** The COO's confirmation of a guess, as the thread shows it. */
export function roleGuessQuestion(guess: RoleGuess): string {
    return `“${guess.kept}” sounds closest to ${WORK_ROLE_LABELS[guess.matched]}. Shall I go with that, or is another role closer?`
}

/** The chips confirming a guess: accept the matched role first, then each of the other three. */
export function roleGuessReplies(guess: RoleGuess): QuickReply[] {
    return [
        { label: `Yes, ${WORK_ROLE_LABELS[guess.matched]}`, value: guess.matched },
        ...WORK_ROLES.filter(role => role !== guess.matched).map(role => ({
            label: WORK_ROLE_LABELS[role],
            value: role,
        })),
    ]
}

/**
 * The setup conversation in the chat view (#1738): the chips of the question it is on, and the kinds of work it
 * offered. Pure, so the view only wires them: which question is open comes from the setup state's saved
 * `work_profile` (`setupQuestion`), and the answers a chip or typed text saves from `setupAnswerFromChip` and
 * `setupAnswerFromTyped`, all in `@zerohuman/shared`, the same rules the API validates against.
 */

/** The chips drawn under the setup conversation: the question's answers, and the free-text chip when it takes one. */
export interface SetupChips {
    replies: QuickReply[]
    /** The label of the chip that opens a typed answer; null when the question is chips only. */
    otherLabel: string | null
}

/** Whether a conversation is the setup conversation. */
export function isSetupChat(chat: { purpose?: string | null } | null | undefined): boolean {
    return chat?.purpose === SETUP_CHAT_PURPOSE
}

/**
 * The quick replies the conversation's stored greeting carries (`payload.quickReplies`): the kinds of work the
 * Catalog offered when it opened, then "Something else". Empty when there is no greeting with chips.
 */
export function greetingReplies(messages: readonly ChatMessageRow[]): QuickReply[] {
    for (const row of messages) {
        const replies = row.role === 'assistant' ? row.payload?.['quickReplies'] : undefined
        if (Array.isArray(replies)) {
            return replies.filter(isQuickReply)
        }
    }
    return []
}

/** The kinds of work the greeting offered, in its order, without "Something else". */
export function offeredKinds(messages: readonly ChatMessageRow[]): string[] {
    return greetingReplies(messages)
        .map(reply => reply.value)
        .filter(value => value !== SOMETHING_ELSE)
}

/**
 * The chips of the open question. The kind of work's are the greeting's own, with "Something else" as the chip
 * that opens a typed answer. The role's are the four roles, with the same typed answer, matched to the closest of them
 * and confirmed before it is saved (`roleGuessFor`, #1742). How technical is chips only. Null once nothing is open.
 */
export function setupChips(question: SetupQuestion | null, messages: readonly ChatMessageRow[]): SetupChips | null {
    if (question === null) {
        return null
    }
    if (question === 'work') {
        const greeting = greetingReplies(messages)
        const other = greeting.find(reply => reply.value === SOMETHING_ELSE)
        return {
            replies: greeting.filter(reply => reply.value !== SOMETHING_ELSE),
            otherLabel: other?.label ?? null,
        }
    }
    return {
        replies: quickRepliesFor(question, []),
        otherLabel: question === 'role' ? SOMETHING_ELSE_LABEL : null,
    }
}

/**
 * The chip beside the proposed teams when the Catalog has no team for the work (#2216): in the conversation it only
 * sends this sentence to the COO, so the COO can help, and saves nothing. Its value is never a catalog team's id.
 */
export const TELL_YOUR_COO = 'Tell your COO what you need'
export const TELL_YOUR_COO_VALUE = 'tell_your_coo'

/** The names answers the names form saves (#2216): the team's name, and one name per teammate, blank for unnamed. */
export interface NamesAnswers {
    teamName: string
    teammateNames: string[]
}

/**
 * The chips of the team proposal (#2216), drawn while setup stands at `team_proposal` with a proposal served: the
 * recommended teams, then the candidates, each labelled by its name and valued by its catalog team id, so a choice
 * saves `{ catalogTeamId }`; then "Tell your COO what you need" when it is offered. Empty elsewhere, and when the
 * state serves no proposal (nothing is drawn from what the state does not hold).
 */
export function teamProposalReplies(state: OnboardingStateView | null | undefined): QuickReply[] {
    const proposal = state?.currentStep === 'team_proposal' ? state.teamProposal : undefined
    if (!proposal) {
        return []
    }
    return [
        ...[...proposal.recommended, ...proposal.candidates].map(offer => ({ label: offer.name, value: offer.id })),
        ...(proposal.tellYourCoo ? [{ label: TELL_YOUR_COO, value: TELL_YOUR_COO_VALUE }] : []),
    ]
}

/**
 * The template the names form names (#2216), while setup stands at `names` with one served; null elsewhere. The
 * form is keyed by it, so a different proposal starts the form afresh.
 */
export function namesTemplateOf(state: OnboardingStateView | null | undefined): NamesTemplate | null {
    return state?.currentStep === 'names' ? (state.namesFor?.template ?? null) : null
}

/**
 * What the person is shown to have said once their names are saved (#2216), sent as their message so the COO's
 * turn runs: the team's name as given, the template's when they left it blank, or that they kept the role titles.
 */
export function namesSaidAs(answers: NamesAnswers | null, template: NamesTemplate): string {
    if (!answers) {
        return 'Keep the role titles'
    }
    return answers.teamName.trim() || template.name
}

/**
 * Whether a refused setup save is the API asking the kind of work first (`WORK_PROFILE_WORK_FIRST`): the person's own
 * profile holds no kind of work, so the chat asks it again whatever the setup state's step says. That happens when
 * the member who began setup left part-way and another member carries on (review of #1953). `request()` throws the
 * refusal's sentence when it carries one and its code otherwise, so either counts.
 */
export function isWorkFirstRefusal(caught: unknown): boolean {
    return (
        caught instanceof Error &&
        (caught.message === WORK_PROFILE_WORK_FIRST || caught.message === WORK_PROFILE_WORK_FIRST_TEXT)
    )
}

function isQuickReply(value: unknown): value is QuickReply {
    if (typeof value !== 'object' || value === null) {
        return false
    }
    const { label, value: chosen } = value as { label?: unknown; value?: unknown }
    return typeof label === 'string' && typeof chosen === 'string'
}
